<script setup>
import Dialog from "dashboard/components-next/dialog/Dialog.vue";
import Input from "dashboard/components-next/input/Input.vue";
import Button from "dashboard/components-next/button/Button.vue";
import CardLayout from "dashboard/components-next/CardLayout.vue";
import ComboBox from "dashboard/components-next/combobox/ComboBox.vue";
import { useAlert } from "dashboard/composables";
import { reactive, ref, computed, onMounted } from "vue";
import { useStore } from "vuex";
import { useMapGetter } from "dashboard/composables/store";
import useVuelidate from "@vuelidate/core";
import { required, minLength, helpers, numeric } from "@vuelidate/validators";
import { parseAPIErrorResponse } from "dashboard/store/utils/api";

const { order, currentChat, message } = defineProps({
  order: {
    type: Object,
    default: null,
  },
  currentChat: {
    type: Object,
    default: null,
  },
  message: {
    type: Object,
    default: null,
  }
});

const store = useStore();
const uiFlags = useMapGetter("giftCards/getUIFlags");
const banks = useMapGetter("giftCards/getBankList");
const creatingOrderWithdraw = computed(
  () => uiFlags.value.creatingOrderWithdraw
);

const fetchBankState = ref(false);

const disabledAccountNumber = computed(() => {
  return (
    message && message.content_attributes?.bank_account?.account_number !== null
  );
});

const accountNumberValue = computed(() => {
  return disabledAccountNumber.value
    ? message.content_attributes?.bank_account?.account_number
    : "";
});

const accountMameValue = computed(() => {
  return disabledAccountNumber.value
    ? message.content_attributes?.bank_account?.account_name
    : "";
});

const accountBankId = computed(() => {
  const bbb = banks.value.length;
  console.log('bbb', bbb);
  if (disabledAccountNumber.value) {
    // 获取要查找的 bank_name
    const targetBankName = message.content_attributes?.bank_account?.bank_name;
    if (targetBankName) {
      const matchedBank = banks.value.find(
        bank => bank.bank_name === targetBankName
      );
      return matchedBank ? String(matchedBank.bank_category_id) : "";
    }
  }
  return '';
});

// 表单数据
const state = reactive({
  bank_id: '',
  account_name: accountMameValue.value,
  account_number: accountNumberValue.value,
  remarks: '',
});

const fetchBankList = async () => {
  await store.dispatch("giftCards/getBankList", {
    country_id: order.country_id,
  });
  fetchBankState.value = true;
  state.bank_id = accountBankId.value;
};

onMounted(() => {
  fetchBankList();
});

const bankOptions = computed(() =>
  banks.value.map(bank => ({
    value: String(bank.bank_category_id),
    label: bank.bank_name,
  }))
);

// 表单验证规则
const rules = {
  bank_id: { required: helpers.withMessage("Bank is required", required) },
  account_name: {
    required: helpers.withMessage("Account name is required", required),
    minLength: helpers.withMessage(
      "Account name must be at least 3 characters",
      minLength(3)
    ),
  },
  account_number: {
    required: helpers.withMessage("Account number is required", required),
    numeric: helpers.withMessage("Account number must be numeric", numeric)
  },
  remarks: {},
};

const statusMap = {
  1: { text: "pending", color: "text-orange-600 bg-orange-100" },
  2: { text: "success", color: "text-teal-600 bg-teal-100" },
  3: { text: "failed", color: "text-red-600 bg-red-100" },
  4: { text: "success", color: "text-teal-600 bg-teal-100" },
};

const statusTextColor = computed(() => {
  return statusMap[order.status]?.color;
});

const statusText = computed(() => {
  return statusMap[order.status]?.text;
});

const withdrawDialogRef = ref(null);
// 初始化 Vuelidate
const v$ = useVuelidate(rules, state);

// 提交表单逻辑
const handleSubmit = async () => {
  v$.value.$touch(); // 标记所有字段为“已接触”
  if (v$.value.$invalid) {
    return;
  }

  try {
    await store.dispatch("giftCards/orderWithdraw", {
      order_no: order.order_no,
      account_name: state.account_name,
      account_no: state.account_number,
      bank_name: state.bank_id,
      bank_id: state.bank_id,
      memo: state.remarks
    });
    Object.assign(state, {
      bank_id: "",
      account_name: "",
      account_number: "",
      remarks: ""
    });
    withdrawDialogRef.value.close();
    useAlert("Withdraw success");
    await store.dispatch("giftCards/getOrderList", {
      conversation_id: currentChat.id
    });
  } catch (error) {
    const errorMessage = parseAPIErrorResponse(error) || "Create order error.";
    useAlert(errorMessage);
  } finally {
  }
};

const withdrawClose = () => {
  Object.assign(state, {
    bank_id: "",
    account_name: "",
    account_number: "",
    remarks: ""
  });
};

defineExpose({
  withdrawDialogRef
});
</script>

<template>
  <Dialog
    ref="withdrawDialogRef"
    type="edit"
    title="Withdraw"
    overflow-y-auto
    width="md"
    :show-cancel-button="false"
    :show-confirm-button="false"
    @close="withdrawClose"
  >
    <div class="space-y-4 bg-gray-50 p-4 rounded">
      <CardLayout layout="row">
        <div class="space-y-2 text-sm">
          <div class="flex justify-between">
            <span class="text-gray-500">Order No:</span>
            <span>{{ order.order_no }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-500">Status:</span>
            <span
              class="inline-flex items-center text-xs font-medium px-3 py-1 rounded-full"
              :class="statusTextColor"
            >
              {{ statusText }}
            </span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-500">Amount:</span>
            <span>{{ order.currency_symbol }} {{ order.purchase_amount }}</span>
          </div>
        </div>
      </CardLayout>
      <form @submit.prevent="handleSubmit" class="space-y-3">
        <ComboBox
          id="bank_id"
          v-model="state.bank_id"
          :options="bankOptions"
          :placeholder="'Select a bank'"
          :label="'Bank'"
          :message="v$.bank_id?.$errors[0]?.$message"
          :hasError="v$.bank_id?.$error"
        />
        <Input
          v-model="state.account_name"
          :label="'Account Name'"
          :placeholder="'Enter Account Name'"
          :message="v$.account_name.$errors[0]?.$message"
          :messageType="v$.account_name.$error ? 'error' : 'info'"
          dense
        />
        <Input
          v-model="state.account_number"
          :disabled="disabledAccountNumber"
          :label="'Account Number'"
          :placeholder="'Enter Account Number'"
          type="number"
          :message="v$.account_number.$errors[0]?.$message"
          :messageType="v$.account_number.$error ? 'error' : 'info'"
          dense
        />
        <Input
          v-model="state.remarks"
          :label="'Remarks'"
          :placeholder="'Optional'"
          dense
        />
        <Button
          type="submit"
          :isLoading="creatingOrderWithdraw"
          :disabled="creatingOrderWithdraw"
          size="sm"
          class="w-full"
        >
          Submit
        </Button>
      </form>
    </div>
  </Dialog>
</template>

<style scoped lang="scss">
/* 将内容块挤压得更紧凑 */
</style>
